/** 
    Function Overview:
    This function calculates the distance between two points on the Earth's surface,
    specified by their latitude and longitude in decimal degrees. It uses the Haversine
    formula to compute the great-circle distance between the points, which is the shortest
    distance over the earth's surface, providing an "as-the-crow-flies" distance between
    the points (ignoring any hills, valleys, or other potential obstacles).

* @returns haversine distance in kilometers
* @category Utils
*/
export function haversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in kilometers, the average radius.

    // Convert latitude and longitude from degrees to radians.
    const dLat = (lat2 - lat1) * Math.PI / 180; // Delta latitude in radians.
    const dLon = (lon2 - lon1) * Math.PI / 180; // Delta longitude in radians.

    // Implementing the Haversine formula:
    // a calculates the square of half the chord length between the points.
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);

    // c calculates the angular distance in radians.
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    // Distance between the two points in kilometers.
    return R * c;
}

/**
 *  
 * @returns haversine distance in meters
 * 
 * @category Utils
 */
export const haversineDistanceMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // metres
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  
    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
    const d = R * c; // returns distance in meters
    return d;
  };

/**
 * Determines whether a coordinate is actually filled in.
 * A coordinate of 0 is treated as not filled in rather than as a real location - 0, 0 is off
 * the coast of Africa and we won't have work at 0 latitude or longitude. Numeric strings are
 * compared as numbers so "0" coming back from a table input is rejected the same as 0.
 *
 * @param {number|string} coordinate
 * @returns {boolean}
 *
 * @category Utils
 */
export const isValidCoordinate = (coordinate) =>
    coordinate !== undefined && coordinate !== null && coordinate !== '' && Number(coordinate) !== 0;


/**
    Function to get the correct points when they are provided from the db
    agm data will have either Est_Lat & Est_Lng or Real_Lat & Real_Lng while
    subchain data has lat & lng

* @category Utils
*/
function getValidCoordinate(real, estimated, fallback) {
    // Return the first valid coordinate based on the provided conditions.
    if (isValidCoordinate(real)) {
        return real;
    } else if (isValidCoordinate(estimated)) {
        return estimated;
    } else {
        return fallback;
    }
}


/**
 * Picks the coordinates an AGM should be drawn at on the map.
 * Real lat/lng wins whenever it is filled in; a Real coord left at 0 (or blank) falls
 * through to the Est coord. If neither is filled in we still return the Est value so the
 * AGM keeps its existing position rather than dropping off the map or becoming NaN.
 *
 * @param {Object} agm - AGM row with Real_Lat/Real_Lng and/or Est_Lat/Est_Lng
 * @returns {Object} { lat, lng }
 *
 * @category Utils
 */
export const getAGMMarkerPosition = (agm) => ({
    lat: getValidCoordinate(agm.Real_Lat, agm.Est_Lat, agm.Est_Lat),
    lng: getValidCoordinate(agm.Real_Lng, agm.Est_Lng, agm.Est_Lng)
});


/**
    Function Overview:
    This function implements the Nearest Neighbor algorithm to find a path that visits all
    given points, starting from the first point and subsequently going to the nearest unvisited
    point until all points are visited. This method is often used in the traveling salesman problem
    to create an approximate path. The function returns an array of points representing the
    order in which the points are visited.

    This function O(n^2) time -> if datasets become larger than 2000 points will need to consider
    another alogorithm

    @category Utils
*/
export function nearestNeighbor(points) {
    if (!Array.isArray(points) || points.length === 0) return [];

    // Copy the array to avoid mutating the original
    let unvisited = points.slice();
    let visited = [];

    // Start with the first point
    let currentPoint = unvisited.shift();
    visited.push(currentPoint);

    // Keep visiting nearest neighbor until all points are visited
    while (unvisited.length > 0) {
        // Get coordinates of current point once
        const currentLat = getValidCoordinate(currentPoint.Real_Lat, currentPoint.Est_Lat, currentPoint.lat);
        const currentLng = getValidCoordinate(currentPoint.Real_Lng, currentPoint.Est_Lng, currentPoint.lng);

        // If current point has invalid coordinates, abort to avoid further errors
        if (currentLat === undefined || currentLng === undefined) {
            console.warn('Invalid coordinates for currentPoint:', currentPoint);
            break;
        }

        // Find the nearest point from remaining unvisited
        let nearest = unvisited.reduce((nearestSoFar, point) => {
            const pointLat = getValidCoordinate(point.Real_Lat, point.Est_Lat, point.lat);
            const pointLng = getValidCoordinate(point.Real_Lng, point.Est_Lng, point.lng);

            // Skip invalid points
            if (pointLat === undefined || pointLng === undefined) return nearestSoFar;

            const distance = haversineDistance(currentLat, currentLng, pointLat, pointLng);
            return distance < nearestSoFar.distance ? { point, distance } : nearestSoFar;
        }, { point: null, distance: Infinity });

        if (!nearest.point) {
            console.warn('No valid nearest neighbor found for:', currentPoint);
            break;
        }

        // Update current and continue-m ""
        currentPoint = nearest.point;
        visited.push(currentPoint);

        // Remove the visited point from unvisited list (compare by reference)
        unvisited = unvisited.filter(p => p !== currentPoint); //eslint-disable-line
    }
    return visited;
}

/**
 * Interpolates between two geographical coordinates (latitude and longitude) by a specified fraction.
 * This function calculates intermediate geographic coordinates by linearly interpolating 
 * between two points based on a fraction that specifies the position between them.
 * 
 * @param {Object} start - The starting coordinate with properties `lat` and `lng` for latitude and longitude.
 * @param {Object} end - The ending coordinate with properties `lat` and `lng` for latitude and longitude.
 * @param {number} fraction - A decimal representing the fraction between the `start` and `end` points
 *                            where 0 is the start point, 1 is the end point, and 0.5 is halfway between.
 * 
 * @returns {Object} An object containing the interpolated latitude (`lat`) and longitude (`lng`).
 * 
 * Example Usage:
 * interpolateLatLng({lat: 34.05, lng: -118.25}, {lat: 40.7128, lng: -74.0060}, 0.5);
 * // Returns approximately {lat: 37.3814, lng: -96.128}
 * 
 * @category Utils
 */
export function interpolateLatLng(start, end, fraction) {
    return {
        lat: start.lat + (end.lat - start.lat) * fraction,
        lng: start.lng + (end.lng - start.lng) * fraction
    };
}

/**
 * Calculates the initial bearing (or forward azimuth) between two geographic points on the Earth's surface.
 * This bearing is the compass direction from the start point to the end point.
 *
 * The calculation is based on the formulae from the spherical Earth projected to a plane model.
 * The result represents the angle in degrees from the North in a clockwise direction.
 * This means a bearing of 0 degrees is North, 90 degrees is East, 180 degrees is South, and 270 degrees is West.
 *
 * @param {number} startLat - The latitude of the start point in decimal degrees.
 * @param {number} startLng - The longitude of the start point in decimal degrees.
 * @param {number} endLat - The latitude of the end point in decimal degrees.
 * @param {number} endLng - The longitude of the end point in decimal degrees.
 *
 * @returns {number} The bearing in degrees between the start and end points, ranging from 0 to 360.
 *
 * Example Usage:
 * calculateBearing(34.05, -118.25, 40.7128, -74.0060);
 * // Returns the bearing from Los Angeles (start point) to New York City (end point).
 * 
 * @category Utils
 */
export function calculateBearing(startLat, startLng, endLat, endLng) {
    const lat1 = startLat * Math.PI / 180; // Convert start latitude to radians
    const lon1 = startLng * Math.PI / 180; // Convert start longitude to radians
    const lat2 = endLat * Math.PI / 180;   // Convert end latitude to radians
    const lon2 = endLng * Math.PI / 180;   // Convert end longitude to radians

    const y = Math.sin(lon2 - lon1) * Math.cos(lat2); // Component of the bearing calculation
    const x = Math.cos(lat1) * Math.sin(lat2) -       // Another component of the bearing calculation
        Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);

    let bearing = Math.atan2(y, x) * 180 / Math.PI;   // Compute the bearing, convert radians to degrees
    return (bearing + 360) % 360;                     // Normalize the bearing to be within the 0-360 degree range
}



function toRadians(degrees) {
    return degrees * (Math.PI / 180);
}

/**
 * 
 * @category Utils 
 */
export function latLngToCartesian(lat, lng) {
    const R = 6371e3; // Earth's radius in meters
    const φ = toRadians(lat);
    const λ = toRadians(lng);
    return {
        x: R * Math.cos(φ) * Math.cos(λ),
        y: R * Math.cos(φ) * Math.sin(λ),
        z: R * Math.sin(φ)
    };
}

function cartesianToLatLng(x, y, z) {
    const R = 6371e3; // Earth's radius in meters
    const lat = Math.asin(z / R);
    const lng = Math.atan2(y, x);
    return {
        lat: lat * (180 / Math.PI),
        lng: lng * (180 / Math.PI)
    };
}

/**
 * 
 * @category Utils 
 */
export const projectPointOnLine = (point, segmentStart, segmentEnd) => {
    const A = latLngToCartesian(segmentStart.lat, segmentStart.lng);
    const B = latLngToCartesian(segmentEnd.lat, segmentEnd.lng);
    const P = latLngToCartesian(point.lat, point.lng);

    const AB = { x: B.x - A.x, y: B.y - A.y, z: B.z - A.z };
    const AP = { x: P.x - A.x, y: P.y - A.y, z: P.z - A.z };

    const AB_lenSq = AB.x * AB.x + AB.y * AB.y + AB.z * AB.z;
    const AP_dot_AB = AP.x * AB.x + AP.y * AB.y + AP.z * AB.z;

    // Calculate t (the projection factor)
    let t = AP_dot_AB / AB_lenSq;

    // Clamp t to the range [0, 1] to ensure the point is on the segment
    t = Math.max(0, Math.min(1, t));

    // Calculate the closest point on the segment
    const closestPoint = {
        x: A.x + t * AB.x,
        y: A.y + t * AB.y,
        z: A.z + t * AB.z
    };

    return cartesianToLatLng(closestPoint.x, closestPoint.y, closestPoint.z);
};

/**
 * 
 * @category Utils 
 */
export const constrainToSegmentBounds = (position, segmentStart, segmentEnd) => {
    const latMin = Math.min(segmentStart.lat, segmentEnd.lat);
    const latMax = Math.max(segmentStart.lat, segmentEnd.lat);
    const lngMin = Math.min(segmentStart.lng, segmentEnd.lng);
    const lngMax = Math.max(segmentStart.lng, segmentEnd.lng);

    // Clamp the latitude and longitude to the bounds of the segment
    const clampedLat = Math.max(latMin, Math.min(position.lat, latMax));
    const clampedLng = Math.max(lngMin, Math.min(position.lng, lngMax));

    return {
        lat: clampedLat,
        lng: clampedLng,
    };
};



/**
 * 
 * @category Utils 
 */
export const findClosestSegment = (point, path) => {
    let closestSegment = null;
    let minDistance = Infinity;
    let segmentIndex = -1;

    for (let i = 0; i < path.length - 1; i++) {
        const segmentStart = path[i];
        const segmentEnd = path[i + 1];

        // Calculate the closest point on the segment using the projection
        const projection = projectPointOnSegment(point, segmentStart, segmentEnd);
        const distance = haversineDistance(point.lat, point.lng, projection.lat, projection.lng);

        if (distance < minDistance) {
            minDistance = distance;
            closestSegment = { upstream: segmentStart, downstream: segmentEnd };
            segmentIndex = i;
        }
    }

    return { closestSegment, segmentIndex };
};

/**
 * 
 * @param {array} agmSubchainAgm - expecting an array with US AGM and DS AGM with subchains in the middle
 * @returns {array} sorted array of agms by OrderNum -> if error then return false to use Nearest Neighbour
 */

export const sortSubchainsByOrderNum = (agmSubchainAgm) => {
    //assume the first and last element is an AGM with no OrderNum in object
    try {
        let usAgm = agmSubchainAgm.shift();
        let dsAgm = agmSubchainAgm.pop();

        agmSubchainAgm.sort((a, b) => a.OrderNum - b.OrderNum);

        agmSubchainAgm.unshift(usAgm);
        agmSubchainAgm.push(dsAgm);

        return agmSubchainAgm;
    } catch (error) {
        return false;
    }
}


const projectPointOnSegment = (point, segmentStart, segmentEnd) => {
    const { lat: lat1, lng: lng1 } = segmentStart;
    const { lat: lat2, lng: lng2 } = segmentEnd;
    const { lat, lng } = point;

    // Calculate distances from the point to the segment endpoints
    const distToStart = haversineDistance(lat, lng, lat1, lng1);
    const distToEnd = haversineDistance(lat, lng, lat2, lng2);

    // Calculate the ratio  
    const ratio = distToStart / (distToStart + distToEnd);

    // Interpolate the closest point on the segment
    const interpolatedLat = lat1 + ratio * (lat2 - lat1);
    const interpolatedLng = lng1 + ratio * (lng2 - lng1);

    // const interpolatedLat = lat1 + (lat2 - lat1) * ratio;
    // const interpolatedLng = lng1 + (lng2 - lng1) * ratio;

    return { lat: interpolatedLat, lng: interpolatedLng };
};

/**
 * Validates a custom intersection point by checking if it is within 10 meters of the pipeline line segment.
 * Uses projectPointOnLine to find the true intersection point on the line, then calculates haversine distance.
 * 
 * @param {Object} customPoint - Custom intersection point { lat: number, lng: number }
 * @param {Object} segmentStart - Line segment start { lat: number, lng: number }
 * @param {Object} segmentEnd - Line segment end { lat: number, lng: number }
 * @param {number} toleranceMeters - Distance tolerance in meters (default: 10)
 * 
 * @returns {Object} { isValid: boolean, distanceMeters: number, errorMessage: string }
 * 
 * @category Utils
 */
export const validateCustomIntersectPoint = (customPoint, segmentStart, segmentEnd, toleranceMeters = 10) => {
    // Get the true intersection point on the line
    const trueIntersect = projectPointOnLine(customPoint, segmentStart, segmentEnd);
    
    // Calculate distance between custom point and true intersection
    const distance = haversineDistanceMeters(customPoint.lat, customPoint.lng, trueIntersect.lat, trueIntersect.lng);

    const isValid = distance <= toleranceMeters;
    
    const errorMessage = !isValid 
        ? `Intersection point is ${distance.toFixed(1)} meters from pipeline. Must be within ${toleranceMeters} meters.`
        : '';
    
    return {
        isValid,
        distanceMeters: distance,
        errorMessage
    };
};

/**
 * Creates a point offset perpendicular to a line segment
 * @param {Object} linePoint - Point on the line {lat, lng}
 * @param {Object} upstreamPoint - Upstream point on the line {lat, lng}
 * @param {Object} downstreamPoint - Downstream point on the line {lat, lng}
 * @param {number} offsetMeters - Distance to offset (default 10)
 * @returns {Object} New point {lat, lng} offset perpendicular
 */
export const createPerpendicularPoint = (linePoint, upstreamPoint, downstreamPoint, offsetMeters = 10) => {
    // Vector along the line
    const dlat = downstreamPoint.lat - upstreamPoint.lat;
    const dlng = downstreamPoint.lng - upstreamPoint.lng;
    
    // Perpendicular vector (rotate 90 degrees) - using lng because lng differences are smaller at equator
    const perpLat = -dlng;
    const perpLng = dlat;
    
    // Normalize
    const magnitude = Math.sqrt(perpLat * perpLat + perpLng * perpLng);
    const normLat = perpLat / magnitude;
    const normLng = perpLng / magnitude;
    
    // Rough conversion: 1 degree ≈ 111,000 meters
    const metersPerDegree = 111000;
    const latOffset = (offsetMeters / metersPerDegree) * normLat;
    const lngOffset = (offsetMeters / metersPerDegree) * normLng;
    
    return {
        lat: linePoint.lat + latOffset,
        lng: linePoint.lng + lngOffset
    };
};